import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/apiClient';
import {
  MessageSquare, Plus, Edit2, Trash2, X, Variable, Copy
} from 'lucide-react';
import PageContainer from '../layout/PageContainer';

interface MessageTemplate {
  id: number;
  name: string;
  content: string;
  category: string;
  variables?: string;
  usage_count: number;
  created_at: string;
  updated_at?: string;
}

const MessageTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    category: 'general'
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/message-templates');
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('テンプレート取得エラー:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await apiClient.put(`/message-templates/${editingTemplate.id}`, formData);
      } else {
        await apiClient.post('/message-templates', formData);
      }
      fetchTemplates();
      resetForm();
      alert(editingTemplate ? 'テンプレートを更新しました' : 'テンプレートを作成しました');
    } catch (error) {
      console.error('保存エラー:', error);
      alert('保存に失敗しました');
    }
  };

  const handleEdit = (template: MessageTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      content: template.content,
      category: template.category
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('このテンプレートを削除しますか？')) return;

    try {
      await apiClient.delete(`/message-templates/${id}`);
      fetchTemplates();
      alert('テンプレートを削除しました');
    } catch (error) {
      console.error('削除エラー:', error);
      alert('削除に失敗しました');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', content: '', category: 'general' });
    setEditingTemplate(null);
    setShowForm(false);
  };

  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content)
      .then(() => alert('コピーしました'))
      .catch(() => alert('コピーに失敗しました'));
  };

  const categoryLabels: { [key: string]: string } = {
    general: '一般',
    promotion: 'プロモーション',
    reminder: 'リマインダー',
    thank_you: 'お礼',
    notification: '通知'
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">読み込み中...</div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2">メッセージテンプレート管理</h2>
          <p className="text-gray-600">LINE配信用のメッセージテンプレートを管理します</p>
        </div>

        <div className="mb-6">
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            新規作成
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <div key={template.id} className="bg-white p-5 rounded-lg shadow-md">
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-semibold text-lg">{template.name}</h3>
                <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                  {categoryLabels[template.category] || template.category}
                </span>
              </div>

              <p className="text-gray-700 text-sm whitespace-pre-wrap line-clamp-4 mb-3">
                {template.content}
              </p>

              <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                <span>使用回数: {template.usage_count}回</span>
                <span>{new Date(template.created_at).toLocaleDateString()}</span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(template)}
                  className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 flex items-center justify-center gap-1"
                >
                  <Edit2 className="w-4 h-4" />
                  編集
                </button>
                <button
                  onClick={() => handleCopy(template.content)}
                  className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 flex items-center justify-center gap-1"
                >
                  <Copy className="w-4 h-4" />
                  コピー
                </button>
                <button
                  onClick={() => handleDelete(template.id)}
                  className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {templates.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>テンプレートがまだありません</p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              最初のテンプレートを作成
            </button>
          </div>
        )}

        {/* 作成・編集フォーム */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold">
                    {editingTemplate ? 'テンプレート編集' : '新規テンプレート'}
                  </h3>
                  <button
                    onClick={resetForm}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">名前</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="テンプレート名を入力"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">カテゴリー</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="general">一般</option>
                      <option value="promotion">プロモーション</option>
                      <option value="reminder">リマインダー</option>
                      <option value="thank_you">お礼</option>
                      <option value="notification">通知</option>
                    </select>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-1">内容</label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="w-full p-2 border rounded-lg h-40 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="メッセージ内容を入力"
                      required
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      ヒント: 変数を使う場合は {'{{名前}}'} の形式で入力できます
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      {editingTemplate ? '更新' : '作成'}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      キャンセル
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default MessageTemplates;